// The provably fair draw, in one place, so the database, the admin page and
// the public "verify this draw" button all do the same arithmetic.
//
//   1. When entries close, the eligible ticket numbers are frozen, sorted,
//      and fingerprinted: SHA-256 of "n1,n2,n3,…" (ascending, no spaces).
//   2. A drand round is named at the same time, from the competition's draw
//      time, so the random value is not known to anyone yet. drand
//      ("quicknet", run by the League of Entropy) publishes a signed random
//      value every 3 seconds; randomness = SHA-256 of the signature.
//   3. Winner k (k = 1, 2, 3 …): SHA-256 of "<randomness>:<competition id>:<k>",
//      first 8 bytes as an unsigned big-endian integer, mod the number of
//      entries, is the position in the sorted list. A ticket already picked
//      is skipped and k moves on.
//
// Pure and browser-safe: Web Crypto only.

export const DRAND = {
  chain: 'quicknet',
  chainHash: '52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971',
  genesis: 1692803367, // unix seconds of round 1
  period: 3, // seconds between rounds
  publicKey:
    '83cf0f2896adee7eb8b5f01fcad3912212c437e0073e911fb90022d3e760183c8c4b450b6a0a6c3ac6a5776a2d1064510d1fec758c921cc22b0e17e63aaf4bcb5ed66304de9cf809bd274ca73bab4af5a6e9c76a4bc09e76eae8991ef5ece45a',
  scheme: 'bls-unchained-g1-rfc9380',
};

/** The first drand round published at or after a moment. */
export function roundAt(date) {
  const seconds = date.getTime() / 1000;
  return Math.max(1, Math.ceil((seconds - DRAND.genesis) / DRAND.period) + 1);
}

/** When a drand round is published. */
export function roundTime(round) {
  return new Date((DRAND.genesis + (Number(round) - 1) * DRAND.period) * 1000);
}

/** Where a round can be fetched from (any one will do; they must agree). */
export function drandUrls(round) {
  return [
    `https://api.drand.sh/v2/beacons/${DRAND.chain}/rounds/${round}`,
    `https://api2.drand.sh/v2/beacons/${DRAND.chain}/rounds/${round}`,
    `https://api3.drand.sh/v2/beacons/${DRAND.chain}/rounds/${round}`,
    `https://drand.cloudflare.com/${DRAND.chainHash}/public/${round}`,
  ];
}

const toHex = (buffer) =>
  [...new Uint8Array(buffer)].map((b) => b.toString(16).padStart(2, '0')).join('');

const fromHex = (hex) =>
  Uint8Array.from(
    String(hex)
      .match(/../g)
      .map((h) => parseInt(h, 16))
  );

async function sha256Hex(input) {
  const bytes = typeof input === 'string' ? new TextEncoder().encode(input) : input;
  return toHex(await globalThis.crypto.subtle.digest('SHA-256', bytes));
}

/** The sorted ticket list as the string that is fingerprinted. */
export function canonicalEntries(tickets) {
  return [...tickets]
    .map(Number)
    .sort((a, b) => a - b)
    .join(',');
}

/** SHA-256 fingerprint (hex) of the entry list. */
export async function entriesHash(tickets) {
  return sha256Hex(canonicalEntries(tickets));
}

/** drand's randomness for a round is the SHA-256 of its signature bytes. */
export async function randomnessFromSignature(signatureHex) {
  return sha256Hex(fromHex(signatureHex));
}

/**
 * The winners for a randomness value: { winners: [ticket…], steps: [...] }.
 * `steps` shows the working for each k, for the verify page.
 */
export async function pickWinners({ randomness, competitionId, tickets, wanted = 1 }) {
  const list = [...tickets].map(Number).sort((a, b) => a - b);
  const n = BigInt(list.length);
  const want = Math.min(Math.max(1, wanted), list.length);
  const winners = [];
  const steps = [];
  for (let k = 1; winners.length < want && k < 10000; k++) {
    const hex = await sha256Hex(`${String(randomness).toLowerCase()}:${competitionId}:${k}`);
    const index = Number(BigInt(`0x${hex.slice(0, 16)}`) % n);
    const ticket = list[index];
    const repeat = winners.includes(ticket);
    steps.push({ k, hash: hex, index, ticket, repeat });
    if (!repeat) winners.push(ticket);
  }
  return { winners, steps };
}

/** Short form of a fingerprint or randomness value for display. */
export const shortHex = (hex, n = 10) => (hex ? `${String(hex).slice(0, n)}…` : '');
